/**
 * NotificationBell — in-app notification center cho user
 * Fallback UX khi user đóng BankTransferModal (ví dụ exception fire sau khi close).
 * Polling /api/notifications mỗi 30s (polling-first, realtime upgrade sau).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, CheckCircle, AlertTriangle, XCircle, Info } from 'lucide-react';

interface NotificationRow {
  id: string;
  type: 'payment_success' | 'payment_partial' | 'payment_overpaid' | 'payment_too_small' | 'payment_unknown_dtl' | 'payment_expired' | 'credits_added_admin' | 'system';
  title: string;
  body: string;
  metadata?: Record<string, any>;
  related_purchase_id?: string | null;
  read_at: string | null;
  created_at: string;
}

interface NotificationBellProps {
  accessToken?: string;
  pollIntervalMs?: number; // default 30s
}

const WORKERS_URL = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';

const TYPE_ICON: Record<NotificationRow['type'], { icon: React.ComponentType<any>; color: string }> = {
  payment_success: { icon: CheckCircle, color: 'text-emerald-600' },
  payment_partial: { icon: CheckCircle, color: 'text-amber-600' },
  payment_overpaid: { icon: AlertTriangle, color: 'text-amber-600' },
  payment_too_small: { icon: XCircle, color: 'text-rose-600' },
  payment_unknown_dtl: { icon: AlertTriangle, color: 'text-orange-600' },
  payment_expired: { icon: Info, color: 'text-slate-500' },
  credits_added_admin: { icon: CheckCircle, color: 'text-indigo-600' },
  system: { icon: Info, color: 'text-slate-500' },
};

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'vừa xong';
  if (mins < 60) return `${mins} phút trước`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} giờ trước`;
  const days = Math.floor(hours / 24);
  return `${days} ngày trước`;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ accessToken, pollIntervalMs = 30_000 }) => {
  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${WORKERS_URL}/api/notifications?limit=20`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch { /* network error — silent */ }
  }, [accessToken]);

  // Initial + polling
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, pollIntervalMs);
    return () => clearInterval(id);
  }, [fetchNotifications, pollIntervalMs]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markRead = async (id: string) => {
    if (!accessToken) return;
    try {
      await fetch(`${WORKERS_URL}/api/notifications/${id}/read`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // Optimistic update
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllRead = async () => {
    if (!accessToken || unreadCount === 0) return;
    try {
      await fetch(`${WORKERS_URL}/api/notifications/mark-all-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const now = new Date().toISOString();
      setNotifications(prev => prev.map(n => n.read_at ? n : { ...n, read_at: now }));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        title="Thông báo"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[360px] max-h-[500px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <h3 className="font-bold text-sm text-slate-800">Thông báo</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">
                Đánh dấu đã đọc tất cả
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-[440px]">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">Chưa có thông báo</div>
            ) : (
              notifications.map(n => {
                const { icon: Icon, color } = TYPE_ICON[n.type] || TYPE_ICON.system;
                const isUnread = !n.read_at;
                return (
                  <button
                    key={n.id}
                    onClick={() => { if (isUnread) markRead(n.id); }}
                    className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex items-start gap-3 ${isUnread ? 'bg-indigo-50/30' : ''}`}
                  >
                    <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${color}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className={`text-sm font-semibold text-slate-800 ${isUnread ? '' : 'opacity-70'}`}>{n.title}</p>
                        {isUnread && <span className="w-2 h-2 rounded-full bg-indigo-500 shrink-0 mt-1.5" />}
                      </div>
                      <p className="text-xs text-slate-600 leading-snug mb-1 line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-slate-400">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
